import type { Addon, Industry, Locale, Plan, PlanId } from './types'

export const PLAN_ORDER: PlanId[] = ['starter', 'growth', 'scale', 'enterprise']

export const PLANS: Plan[] = [
  {
    id: 'starter',
    name: 'Starter',
    tagline: 'For small teams running support on one or two numbers.',
    baseCents: 4900,
    includedSeats: 3,
    includedNumbers: 1,
    includedConversations: 2000,
    seatCents: 1200,
    numberCents: 2500,
    conversationBlockCents: 600,
    maxSeats: 10,
    maxNumbers: 3,
    bundledAddons: [],
    features: [
      'Shared inbox for 1 number',
      'Group and 1:1 conversations',
      'Contacts and tagging',
      'Canned replies',
      'Email support',
    ],
  },
  {
    id: 'growth',
    name: 'Growth',
    tagline: 'Multi-number inbox with automation and CRM sync.',
    baseCents: 14900,
    includedSeats: 10,
    includedNumbers: 3,
    includedConversations: 10000,
    seatCents: 1000,
    numberCents: 2000,
    conversationBlockCents: 500,
    maxSeats: 50,
    maxNumbers: 15,
    bundledAddons: ['crm_sync'],
    popular: true,
    features: [
      'Everything in Starter',
      'Multi-number shared inbox',
      'Automation rules and auto-assign',
      'Tickets and SLA tracking',
      'CRM sync included',
      'Analytics dashboard',
    ],
  },
  {
    id: 'scale',
    name: 'Scale',
    tagline: 'AI agents, MCP access, and audit logging for large operations.',
    baseCents: 44900,
    includedSeats: 30,
    includedNumbers: 10,
    includedConversations: 50000,
    seatCents: 800,
    numberCents: 1500,
    conversationBlockCents: 400,
    maxSeats: 250,
    maxNumbers: 100,
    bundledAddons: ['crm_sync', 'ai_agent', 'mcp_server', 'audit_log'],
    features: [
      'Everything in Growth',
      'AI agent with custom knowledge base',
      'MCP server for Claude and agent frameworks',
      'Immutable audit log',
      'Role-based permissions and number masking',
      'Priority support with 4h SLA',
    ],
  },
  {
    id: 'enterprise',
    name: 'Enterprise',
    tagline: 'Regulated deployments with residency, SSO, and custom terms.',
    baseCents: 0,
    includedSeats: 100,
    includedNumbers: 50,
    includedConversations: 250000,
    seatCents: 600,
    numberCents: 1200,
    conversationBlockCents: 300,
    maxSeats: null,
    maxNumbers: null,
    contactSales: true,
    bundledAddons: [
      'crm_sync',
      'ai_agent',
      'mcp_server',
      'audit_log',
      'sso_scim',
      'data_residency',
      'priority_support',
    ],
    features: [
      'Everything in Scale',
      'Regional data residency',
      'SSO and SCIM provisioning',
      'HIPAA or FINRA modules available',
      'Dedicated success manager',
      'Custom contract and DPA',
    ],
  },
]

export const ADDONS: Addon[] = [
  {
    id: 'ai_agent',
    name: 'AI Agent',
    description:
      'Trains on your FAQs and SOPs to answer repetitive queries, draft replies, and raise tickets.',
    cents: 9900,
    requiredFor: [],
    minPlan: 'growth',
    category: 'ai',
  },
  {
    id: 'mcp_server',
    name: 'MCP Server',
    description:
      'Exposes your workspace to Claude and agent frameworks over the Model Context Protocol.',
    cents: 4900,
    requiredFor: [],
    minPlan: 'growth',
    category: 'ai',
  },
  {
    id: 'crm_sync',
    name: 'CRM Sync',
    description:
      'Pushes conversations into HubSpot, Freshdesk, and Zoho automatically, with two-way field sync for Zoho CRM.',
    cents: 5900,
    requiredFor: [],
    minPlan: 'starter',
    category: 'integration',
  },
  {
    id: 'audit_log',
    name: 'Immutable Audit Log',
    description:
      'Append-only, tamper-evident record of every message, action, and export with retention controls.',
    cents: 12900,
    requiredFor: ['financial', 'healthcare'],
    minPlan: 'growth',
    category: 'compliance',
  },
  {
    id: 'hipaa_wrapper',
    name: 'HIPAA Module',
    description:
      'BAA, PHI redaction, device restrictions, and access review workflows for covered entities.',
    cents: 79900,
    requiredFor: ['healthcare'],
    minPlan: 'scale',
    category: 'compliance',
  },
  {
    id: 'finra_archive',
    name: 'FINRA / SEC Archive',
    description:
      'WORM-compliant retention, supervisory review queues, and regulator-ready exports.',
    cents: 99900,
    requiredFor: ['financial'],
    minPlan: 'scale',
    category: 'compliance',
  },
  {
    id: 'data_residency',
    name: 'Regional Data Residency',
    description:
      'Pins storage and processing to your region. Required for most GDPR and DPDP deployments.',
    cents: 29900,
    requiredFor: [],
    minPlan: 'scale',
    category: 'compliance',
  },
  {
    id: 'sso_scim',
    name: 'SSO and SCIM',
    description: 'OIDC single sign-on with automated SCIM user provisioning and deprovisioning.',
    // Self-serve as of 2026-08-15 (lib/oidc.ts + lib/scim.ts): OIDC login and SCIM Users
    // are real. Full SAML remains a sales-assisted increment beyond this addon.
    cents: 19900,
    requiredFor: [],
    minPlan: 'scale',
    category: 'platform',
  },
  {
    id: 'priority_support',
    name: 'Priority Support',
    description: 'One-hour first response, named engineer, and a quarterly operations review.',
    cents: 24900,
    requiredFor: [],
    minPlan: 'growth',
    category: 'platform',
  },
]

export const INDUSTRIES: Industry[] = [
  {
    id: 'general',
    name: 'General Business',
    description: 'Sales, support, and internal operations without sector-specific rules.',
    icon: 'business',
    loadMultiplier: 1.0,
    compliance: ['none'],
    presets: {
      plan: 'growth',
      seats: 10,
      numbers: 3,
      conversations: 10000,
      addons: ['crm_sync'],
    },
    presetRationale:
      'Growth covers a full team on a handful of numbers with automation and CRM sync included.',
    workflowTemplates: ['Shared inbox triage', 'Lead qualification', 'Post-sale follow-up'],
  },
  {
    id: 'ecommerce',
    name: 'E-commerce and Retail',
    description: 'Cart recovery, order updates, and post-purchase support at high message volume.',
    icon: 'shopping',
    loadMultiplier: 1.05,
    compliance: ['none'],
    presets: {
      plan: 'growth',
      seats: 12,
      numbers: 4,
      conversations: 40000,
      addons: ['crm_sync', 'ai_agent'],
    },
    presetRationale:
      'Retail volume is bursty and message-heavy, so the preset front-loads conversation capacity and adds the AI agent for repetitive order queries.',
    workflowTemplates: [
      'Abandoned cart recovery',
      'Back-in-stock alerts',
      'Order status and returns',
      'Win-back campaigns',
    ],
  },
  {
    id: 'education',
    name: 'Education and Training',
    description: 'Enrolment, trial booking, parent updates, and cohort communication.',
    icon: 'school',
    loadMultiplier: 1.0,
    compliance: ['gdpr'],
    presets: {
      plan: 'growth',
      seats: 15,
      numbers: 5,
      conversations: 25000,
      addons: ['crm_sync', 'ai_agent'],
    },
    presetRationale:
      'Programme managers each run their own cohorts, so seats matter more than numbers. The AI agent handles the repetitive enrolment funnel.',
    workflowTemplates: [
      'Enquiry to trial booking',
      'Parent progress updates',
      'Cohort announcements',
      'Fee reminders',
    ],
  },
  {
    id: 'healthcare',
    name: 'Healthcare',
    description: 'Patient-initiated scheduling and follow-up under HIPAA-aligned controls.',
    icon: 'health',
    loadMultiplier: 1.35,
    compliance: ['hipaa', 'gdpr'],
    presets: {
      plan: 'scale',
      seats: 25,
      numbers: 8,
      conversations: 30000,
      addons: ['hipaa_wrapper', 'audit_log', 'data_residency', 'sso_scim'],
    },
    presetRationale:
      'Covered entities need the HIPAA module, audit log, and residency controls before any patient message is handled. These are pre-selected and cannot be removed.',
    workflowTemplates: [
      'Appointment scheduling',
      'Pre-visit intake',
      'Post-discharge follow-up',
      'Prescription refill requests',
    ],
  },
  {
    id: 'financial',
    name: 'Financial Services',
    description: 'Advisory, lending, and relationship banking with supervised record-keeping.',
    icon: 'finance',
    loadMultiplier: 1.45,
    compliance: ['sox_finra', 'gdpr'],
    presets: {
      plan: 'scale',
      seats: 30,
      numbers: 12,
      conversations: 20000,
      addons: ['finra_archive', 'audit_log', 'data_residency', 'sso_scim'],
    },
    presetRationale:
      'Off-channel communication is the single largest source of regulatory penalties in this sector. The archive and audit log are mandatory and pre-selected.',
    workflowTemplates: [
      'Client onboarding and KYC',
      'Supervised advisory chat',
      'Loan application status',
      'Approval escalation chains',
    ],
  },
  {
    id: 'field_services',
    name: 'Field Services',
    description: 'Dispatch, technician coordination, and on-site job updates.',
    icon: 'tools',
    loadMultiplier: 1.1,
    compliance: ['none'],
    presets: {
      plan: 'growth',
      seats: 20,
      numbers: 8,
      conversations: 15000,
      addons: ['crm_sync', 'ai_agent'],
    },
    presetRationale:
      'Dispatchers and technicians each need a seat and crews are split across numbers, so the preset weights seats and numbers over raw volume.',
    workflowTemplates: [
      'Job dispatch and routing',
      'Technician check-in',
      'Customer arrival notice',
      'Completion and survey',
    ],
  },
  {
    id: 'logistics',
    name: 'Logistics and Transport',
    description: 'Fleet coordination, driver comms, and delivery exception handling.',
    icon: 'truck',
    // Same 1.1x as field services: no statutory regime to carry, but incident traffic runs
    // around the clock and drivers escalate from the road, so support load sits above general.
    loadMultiplier: 1.1,
    compliance: ['none'],
    presets: {
      plan: 'growth',
      seats: 20,
      // More numbers than field services — depots and shifts each hold their own line.
      numbers: 10,
      conversations: 20000,
      addons: ['crm_sync', 'ai_agent'],
    },
    presetRationale:
      'Depots and shifts each run their own number and drivers message from the road at all hours, so the preset weights numbers and volume over seats.',
    workflowTemplates: [
      'Driver incident escalation',
      'Delivery exception',
      'Route and ETA change',
      'Proof of delivery chase',
    ],
  },
]

export const LOCALES: Locale[] = [
  {
    id: 'us',
    name: 'United States',
    region: 'North America',
    currency: 'USD',
    currencySymbol: '$',
    minorUnits: 100,
    fxRate: 1,
    pppMultiplier: 1,
    taxLabel: 'Sales tax',
    taxRate: 0,
    compliance: ['none'],
    roundTo: 1,
  },
  {
    id: 'eu',
    name: 'European Union',
    region: 'Europe',
    currency: 'EUR',
    currencySymbol: '€',
    minorUnits: 100,
    fxRate: 0.92,
    pppMultiplier: 1.0,
    taxLabel: 'VAT',
    taxRate: 0.21,
    compliance: ['gdpr'],
    roundTo: 1,
  },
  {
    id: 'uk',
    name: 'United Kingdom',
    region: 'Europe',
    currency: 'GBP',
    currencySymbol: '£',
    minorUnits: 100,
    fxRate: 0.79,
    pppMultiplier: 1.0,
    taxLabel: 'VAT',
    taxRate: 0.2,
    compliance: ['gdpr'],
    roundTo: 1,
  },
  {
    id: 'in',
    name: 'India',
    region: 'Asia Pacific',
    currency: 'INR',
    currencySymbol: '₹',
    minorUnits: 100,
    fxRate: 86,
    pppMultiplier: 0.4,
    taxLabel: 'GST',
    taxRate: 0.18,
    compliance: ['dpdp'],
    roundTo: 10,
  },
  {
    id: 'ae',
    name: 'United Arab Emirates',
    region: 'Middle East',
    currency: 'AED',
    currencySymbol: 'AED',
    minorUnits: 100,
    fxRate: 3.67,
    pppMultiplier: 0.9,
    taxLabel: 'VAT',
    taxRate: 0.05,
    compliance: ['none'],
    roundTo: 5,
  },
  {
    id: 'sg',
    name: 'Singapore',
    region: 'Asia Pacific',
    currency: 'SGD',
    currencySymbol: 'S$',
    minorUnits: 100,
    fxRate: 1.35,
    pppMultiplier: 0.95,
    taxLabel: 'GST',
    taxRate: 0.09,
    compliance: ['pdpa'],
    roundTo: 1,
  },
  {
    id: 'br',
    name: 'Brazil',
    region: 'Latin America',
    currency: 'BRL',
    currencySymbol: 'R$',
    minorUnits: 100,
    fxRate: 5.9,
    pppMultiplier: 0.5,
    taxLabel: 'ISS',
    taxRate: 0.05,
    compliance: ['lgpd'],
    roundTo: 5,
  },
]

export const COMPLIANCE_LABELS: Record<string, string> = {
  none: 'No sector mandate',
  gdpr: 'GDPR',
  hipaa: 'HIPAA',
  sox_finra: 'SEC / FINRA',
  pdpa: 'PDPA',
  lgpd: 'LGPD',
  dpdp: 'DPDP',
}

export const ANNUAL_DISCOUNT = 0.17

export const getPlan = (id: string) => PLANS.find((p) => p.id === id)
export const getIndustry = (id: string) => INDUSTRIES.find((i) => i.id === id)
export const getLocale = (id: string) => LOCALES.find((l) => l.id === id)
export const getAddon = (id: string) => ADDONS.find((a) => a.id === id)
export const planRank = (id: PlanId) => PLAN_ORDER.indexOf(id)
